import {Component, computed, effect, inject, OnDestroy, OnInit} from '@angular/core';
import {ArchitecturalPrinciple, DirectusService, HrTool, HrToolFilters} from '@frontend/directus';
import {TranslocoTranslationService} from '@frontend/transloco';
import {firstValueFrom, Subject, Subscription, takeUntil} from 'rxjs';
import {APPLICATION_TYPES, HrToolOverviewMode, HrToolService} from './hr-tool.service';
import {debounceTime, distinctUntilChanged} from 'rxjs/operators';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {HrToolSelectionComponent} from './hr-tool-selection/hr-tool-selection.component';
import {HrToolOverviewComponent} from './hr-tool-overview/hr-tool-overview.component';
import {HrToolFilterComponent} from './hr-tool-filter/hr-tool-filter.component';
import {HrToolInfoBoxComponent} from './hr-tool-info-box/hr-tool-info-box.component';
import {GlobalSearchComponent} from '../../shared/components/search/global-search/global-search.component';

@Component({
  selector: 'app-hr-tool',
  imports: [
    TranslocoPipe,
    HrToolSelectionComponent,
    HrToolOverviewComponent,
    HrToolFilterComponent,
    HrToolInfoBoxComponent,
    GlobalSearchComponent,
  ],
  templateUrl: './hr-tool.component.html',
})
export class HrToolComponent implements OnInit, OnDestroy {
  private readonly directusService = inject(DirectusService);
  private readonly translocoManualTranslation = inject(TranslocoTranslationService);
  private readonly transloco = inject(TranslocoService);
  private readonly hrToolsService = inject(HrToolService);

  private readonly langSubscription!: Subscription;
  private readonly destroy$ = new Subject<void>();
  private readonly searchInput$ = new Subject<string>();

  protected readonly providerCount = computed(() => this.hrToolsService.providerGroups().length);
  protected readonly toolCount = computed(() => this.hrToolsService.toolTiles().length);
  protected readonly applicationTypeFilter = this.hrToolsService.applicationTypeFilter;
  protected readonly applicationTypes = APPLICATION_TYPES;
  protected readonly overviewMode = this.hrToolsService.overviewMode;
  protected readonly isToolsOverview = computed(() => this.overviewMode() === 'tools');
  protected readonly isProvidersOverview = computed(() => this.overviewMode() === 'providers');
  protected readonly searchPlaceholderKey = computed(() =>
    this.isToolsOverview() ? 'hrTool.search.products' : 'hrTool.search.providers',
  );

  constructor() {
    this.setupSearchListener();

    effect(() => {
      this.onFilterChange();
    });
  }

  ngOnInit() {
    this.hrToolsService.loadProviders();
    this.hrToolsService.ensureArchitecturalPrinciplesLoaded();
    this.translocoManualTranslation.handleLanguageChange(this.langSubscription, (lang) =>
      this.hrToolsService.updateHrToolTranslations(lang),
    );
  }

  protected setOverviewMode(mode: HrToolOverviewMode): void {
    this.hrToolsService.overviewMode.set(mode);
  }

  protected toggleApplicationType(type: ArchitecturalPrinciple): void {
    this.hrToolsService.toggleApplicationType(type);
  }

  protected isApplicationTypeActive(type: ArchitecturalPrinciple): boolean {
    return this.applicationTypeFilter().has(type);
  }

  protected applicationTypeIcon(type: ArchitecturalPrinciple): string {
    return this.hrToolsService.applicationTypeIcon(type);
  }

  protected applicationTypeLabel(type: ArchitecturalPrinciple): string {
    return this.hrToolsService.applicationTypeLabel(type);
  }

  loadHrTools(filters: HrToolFilters = this.hrToolsService.filters()) {
    const activeFilters: HrToolFilters = {
      ...filters,
      active: true,
    };

    this.directusService.loadHrTools(activeFilters).subscribe({
      next: (response: HrTool[]) => {
        this.hrToolsService.updateHrTools(response);
        if (!activeFilters.aiCapabilities?.length) {
          this.hrToolsService.aiCapabilityFacetTools.set(response);
        }

        this.translocoManualTranslation.translateForActiveLanguage((lang) =>
          this.hrToolsService.updateHrToolTranslations(lang),
        );
      },
    });
    void this.loadAiCapabilityFacetTools(activeFilters);
  }

  private async loadAiCapabilityFacetTools(filters: HrToolFilters): Promise<void> {
    if (!filters.aiCapabilities?.length) {
      return;
    }
    const tools = await firstValueFrom(
      this.directusService.loadHrTools({...filters, aiCapabilities: undefined}),
    );
    this.hrToolsService.aiCapabilityFacetTools.set(tools);
  }

  onSearchChange(searchValue: string): void {
    this.searchInput$.next(searchValue);
  }

  setupSearchListener(): void {
    this.searchInput$
      .pipe(debounceTime(500), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((searchValue) => {
        const lang = this.transloco.getActiveLang();
        this.hrToolsService.filters.update((filters) => ({
          ...filters,
          nameSearch: {input: searchValue, languageCode: lang},
        }));
      });
  }

  onFilterChange() {
    this.loadHrTools(this.hrToolsService.filters());
  }

  ngOnDestroy() {
    this.translocoManualTranslation.removeLanguageSubscription(this.langSubscription);
    this.destroy$.next();
    this.destroy$.complete();
  }
}
